import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const textureImage = "https://images.pexels.com/photos/25752976/pexels-photo-25752976.jpeg?auto=compress&cs=tinysrgb&w=800&h=600";

const philosophyItems = [
"Own the land",
"Design the space",
"Supply the materials",
"Build the environment",
"Operate the experience",
"Shape the culture"];


export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="about"
      data-testid="about-section"
      ref={ref}
      className="py-24 lg:py-32 bg-[#F9F6F0]">

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left Column - Text Content */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            <motion.p
              className="overline mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}>

              About Us
            </motion.p>

            <motion.h2
              data-testid="about-title"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1A1A1A] leading-[1.2]"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}>

              A Full-Stack Ecosystem
            </motion.h2>

            <div className="gold-line mt-6 mb-8" />

            <motion.p
              data-testid="about-description"
              className="text-base font-medium text-[#4A4A4A] leading-relaxed tracking-wide"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}>

              Unlike traditional companies that focus on only one layer of the value chain, 
              Hemraj Grandsons is designed as a complete ecosystem. We create a rare level of 
              control, consistency, and quality across every layer of the customer experience.
            </motion.p>

            <motion.p
              className="mt-4 text-base font-medium text-[#4A4A4A] leading-relaxed tracking-wide"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}>

              The group operates across real estate, design, building materials, hospitality, 
              services, technology, fashion, media, and culture — creating spaces where people 
              live, stay, work, gather, and express themselves.
            </motion.p>
          </div>

          {/* Right Column - Visual Grid */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Philosophy Card */}
              <motion.div
                data-testid="philosophy-card"
                className="sm:col-span-2 bg-[#1A1A1A] p-8 lg:p-12"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}>

                <p className="overline mb-6 !text-[#E7E7E7]">Our Philosophy</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {philosophyItems.map((item, index) =>
                  <motion.div
                    key={item}
                    className="text-[#F9F6F0] font-heading text-lg sm:text-xl font-light"
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}>

                      <span className="text-[#C5A880] mr-2">{String(index + 1).padStart(2, '0')}</span>
                      {item}
                    </motion.div>
                  )}
                </div>
              </motion.div>

              {/* Image Card */}
              <motion.div
                className="image-zoom aspect-square"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}>

                <img
                  src={textureImage}
                  alt="Sandstone texture"
                  className="w-full h-full object-cover" />

              </motion.div>

              {/* Stats Card */}
              <motion.div
                data-testid="stats-card"
                className="bg-[#EFEBE4] p-8 flex flex-col justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.7 }}>

                <div className="space-y-6">
                  <div>
                    <p className="font-heading text-4xl lg:text-5xl font-light text-[#1A1A1A]">15+</p>
                    <p className="text-sm text-[#4A4A4A] mt-1">Companies</p>
                  </div>
                  <div>
                    <p className="font-heading text-4xl lg:text-5xl font-light text-[#1A1A1A]">6</p>
                    <p className="text-sm text-[#4A4A4A] mt-1">Industries</p>
                  </div>
                  <div>
                    <p className="font-heading text-4xl lg:text-5xl font-light text-[#1A1A1A]">1</p>
                    <p className="text-sm text-[#4A4A4A] mt-1">Unified Vision</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};